import { createSignal, Show } from "solid-js";
import styles from "./InlineEdit.module.css";
import { useTranslation } from "../hooks/useTranslation";

interface InlineEditProps {
  value: string;
  placeholder?: string;
  onSave: (next: string) => Promise<void> | void;
  validate?: (next: string) => string | null;
  multiline?: boolean;
  ariaLabel?: string;
}

export function InlineEdit(props: InlineEditProps) {
  const { t } = useTranslation();
  const [editing, setEditing] = createSignal(false);
  const [draft, setDraft] = createSignal(props.value);
  const [saving, setSaving] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);

  function startEdit() {
    setDraft(props.value);
    setError(null);
    setEditing(true);
  }

  function cancel() {
    setDraft(props.value);
    setError(null);
    setEditing(false);
  }

  async function commit() {
    const next = draft();
    if (next === props.value) {
      setEditing(false);
      return;
    }
    if (props.validate) {
      const err = props.validate(next);
      if (err) { setError(err); return; }
    }
    setSaving(true);
    try {
      await props.onSave(next);
      setEditing(false);
    } catch (e) {
      setError((e as Error).message ?? t("INLINE_EDIT_SAVE_FAILED"));
    } finally {
      setSaving(false);
    }
  }

  function onKey(e: KeyboardEvent) {
    if (e.key === "Enter" && !props.multiline) {
      e.preventDefault();
      commit();
    } else if (e.key === "Escape") {
      e.preventDefault();
      cancel();
    } else if (e.key === "Enter" && props.multiline && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      commit();
    }
  }

  return (
    <Show when={editing()} fallback={
      <button class={styles.viewer} onClick={startEdit} aria-label={props.ariaLabel ?? "Edit"}>
        <span class={styles.value}>{props.value || <em class={styles.placeholder}>{props.placeholder ?? "-"}</em>}</span>
        <i class={`fa-solid fa-pen ${styles.pen}`} aria-hidden="true" />
      </button>
    }>
      <div class={styles.editor}>
        <Show when={props.multiline} fallback={
          <input
            class={styles.input}
            type="text"
            value={draft()}
            onInput={(e) => setDraft(e.currentTarget.value)}
            onKeyDown={onKey}
            onBlur={commit}
            disabled={saving()}
            // @ts-ignore solid autofocus
            autofocus
          />
        }>
          <textarea
            class={styles.textarea}
            value={draft()}
            onInput={(e) => setDraft(e.currentTarget.value)}
            onKeyDown={onKey}
            onBlur={commit}
            disabled={saving()}
            // @ts-ignore solid autofocus
            autofocus
          />
        </Show>
        <Show when={error()}>
          <span class={styles.error}>{error()}</span>
        </Show>
      </div>
    </Show>
  );
}
